import { getChainConfig } from '../config/chainConfig';

class BlockchainService {
  constructor(chainId) {
    this.chainId = chainId;
    this.chainConfig = getChainConfig(chainId);
    this.apiUrl = process.env.REACT_APP_API_URL;
  }

  // Simple fetch method - let backend handle all rate limiting
  async fetchFromChain(path, body = null) {
    const url = `${this.apiUrl}/${this.chainId}${path}`;
    // fetch no tiene opción timeout. 65s: por encima del timeout más largo del backend (60s)
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 65000);
    const options = {
      method: body ? 'POST' : 'GET',
      headers: {
        'Content-Type': 'application/json'
      },
      body: body ? JSON.stringify(body) : undefined,
      signal: controller.signal
    };

    try {
      const response = await fetch(url, options);
      // Un proxy puede devolver HTML en un 502/504: no dejar que el parseo tape el status
      const data = await response.json().catch((parseError) => {
        if (response.ok) throw parseError;
        return {};
      });
      
      if (!response.ok) {
        // Create error with appropriate message based on status
        let errorMessage;
        
        if (response.status === 429) {
          errorMessage = `Too many requests to ${this.chainConfig.name} API. Please wait a moment.`;
        } else if (response.status >= 500) {
          errorMessage = `Server error (${response.status}): ${this.chainConfig.name} API temporarily unavailable`;
        } else if (response.status === 404) {
          errorMessage = `Resource not found on ${this.chainConfig.name} API`;
        } else {
          errorMessage = data.error || `API error (${response.status}): ${this.chainConfig.name} service unavailable`;
        }
        
        const error = new Error(errorMessage);
        error.statusCode = response.status;
        error.isRateLimitError = response.status === 429;
        error.isServerError = response.status >= 500;
        throw error;
      }
      
      return data;
      
    } catch (error) {
      // Handle network errors
      if (error.name === 'AbortError' || error.name === 'TypeError' || error.message.includes('fetch')) {
        const networkError = new Error(`Network error connecting to ${this.chainConfig.name} API: ${error.message}`);
        networkError.isNetworkError = true;
        throw networkError;
      }
      
      // Re-throw API errors
      throw error;
    } finally {
      clearTimeout(timer);
    }
  }

  // Get simple API status - backend will provide detailed status
  getApiStatus() {
    return {
      status: 'healthy',
      message: 'API connection active'
    };
  }

  // Cuentas que usan una clave pública: [{ account, permission, weight, threshold }]
  async getKeyAccounts(key) {
    return this.fetchFromChain(`/key_accounts/${encodeURIComponent(key)}`);
  }

  async searchAccounts(searchTerm) {
    return this.fetchFromChain('/search_accounts', { search: searchTerm });
  }

  async getInfo() {
    return this.fetchFromChain('/info');
  }

  async getBlock(num) {
    return this.fetchFromChain(`/get_block/${num}`);
  }

  // Resolves an ambiguous 64-hex id (transaction id or block hash) to its type.
  async resolveHash(id) {
    return this.fetchFromChain(`/resolve_hash/${id}`);
  }

  getChainFamily() {
    return this.chainConfig.chainFamily;
  }

  getChainName() {
    return this.chainConfig.name;
  }

  getChainSymbol() {
    return this.chainConfig.symbol;
  }

  getBlockExplorerUrl() {
    return this.chainConfig.blockExplorerUrl;
  }
}

export default BlockchainService;
